import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import AuthLayout from '../../components/AuthLayout';
import { expertAuthApi, getErrorMessage } from '../../lib/http';

// Validation schema
const forgotSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type ForgotFormData = z.infer<typeof forgotSchema>;

const ExpertForgot: React.FC = () => {
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotFormData>();

  const onSubmit = async (data: ForgotFormData) => {
    setError('');
    setSuccess(false);
    setIsLoading(true);

    try {
      // Call API
      await expertAuthApi.forgot({ email: data.email });

      setSuccess(true);
    } catch (err: any) {
      setError(getErrorMessage(err));
    } finally{
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout role="expert" title="Forgot Password">
      {success ? (
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Check your email
          </h3>

          <p className="text-gray-600 mb-6">
            We've sent a password reset link to your email address.
            Please check your inbox and follow the instructions.
          </p>

          <Link
            to="/expert/login"
            className="inline-block text-pink-600 hover:underline font-medium"
          >
            Back to login
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          <p className="text-sm text-gray-600 mb-4">
            Enter your email address and we'll send you a link to reset your password.
          </p>

          {/* Error Alert */}
          {error && (
            <div
              role="alert"
              className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm"
              aria-live="polite"
            >
              {error}
            </div>
          )}

          {/* Email Field */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Email
            </label>
            <input
              {...register('email')}
              id="email"
              type="email"
              autoComplete="email"
              className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-colors ${
                errors.email ? 'border-red-300' : 'border-gray-300'
              }`}
              aria-invalid={errors.email ? 'true' : 'false'}
              aria-describedby={errors.email ? 'email-error' : undefined}
            />
            {errors.email && (
              <p id="email-error" className="mt-1 text-sm text-red-600">
                {errors.email.message}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-pink-600 hover:bg-pink-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-4 focus:ring-pink-200"
          >
            {isLoading ? 'Sending...' : 'Send reset link'}
          </button>

          {/* Links */}
          <div className="text-center text-sm">
            <Link
              to="/expert/login"
              className="text-pink-600 hover:underline"
            >
              Back to login
            </Link>
          </div>
        </form>
      )}
    </AuthLayout>
  );
};

export default ExpertForgot;
